import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { authApi } from '@/api/auth'
import { api } from '@/api/client'
import { AppShell } from '@/components/AppShell'
import { Alert, Button, Field } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { enablePush } from '@/features/notifications/useNotifications'
import { formatDate } from '@/features/orders/status'
import { THEMES, useTheme } from '@/lib/theme'
import { useAuthStore } from '@/stores/authStore'

interface Subscription {
  plan: string
  status: string
  trialEndsAt: string | null
  currentPeriodEnd: string | null
  grantsPortalAccess: boolean
}

/** Ρυθμίσεις, συνδρομή και δικαιώματα GDPR (§11, οθόνη 10). */
export default function SettingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const session = useAuthStore((s) => s.session)
  const setSession = useAuthStore((s) => s.setSession)
  const isOwner = useAuthStore((s) => s.hasPermission('org:update'))

  const [confirmation, setConfirmation] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [pushState, setPushState] = useState<string | null>(null)

  const subscription = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<Subscription>('/subscription'),
  })

  const logout = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      setSession(null)
      queryClient.clear()
      navigate('/login', { replace: true })
    },
  })

  // Ένας χρήστης μπορεί να είναι και επιχείρηση και προμηθευτής (π.χ. ο
  // ιδιοκτήτης που δοκιμάζει και τις δύο πλευρές). Η εναλλαγή γίνεται εδώ,
  // χωρίς αποσύνδεση: νέα συνεδρία, καθαρή cache, πίσω στην Αρχική.
  const switchOrganization = useMutation({
    mutationFn: authApi.switchOrganization,
    onSuccess: (next) => {
      setSession(next)
      queryClient.clear()
      navigate('/', { replace: true })
    },
  })

  const deleteAccount = useMutation({
    mutationFn: () => api.post<{ message: string }>('/account/delete', { confirmation }),
    onSuccess: () => {
      setSession(null)
      queryClient.clear()
      navigate('/login', { replace: true })
    },
  })

  const organizationName =
    session?.memberships.find((m) => m.organizationId === session.organizationId)
      ?.organizationName ?? ''
  const theme = useTheme((state) => state.theme)
  const setTheme = useTheme((state) => state.setTheme)

  return (
    <AppShell title={t('settings.title')}>
      <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
          {t('settings.subscription')}
        </h2>

        {subscription.isPending && <p className="text-gray-500">{t('common.loading')}</p>}

        {subscription.data && (
          <dl className="space-y-2 text-sm">
            <Row
              label={t('settings.plan')}
              value={t(`settings.plans.${subscription.data.plan}`)}
            />
            <Row
              label={t('settings.status')}
              value={t(`settings.statuses.${subscription.data.status}`)}
            />
            {subscription.data.trialEndsAt && (
              <Row
                label={t('settings.trialEnds')}
                value={formatDate(subscription.data.trialEndsAt)}
              />
            )}
          </dl>
        )}

        {subscription.data && !subscription.data.grantsPortalAccess && (
          <div className="mt-3">
            <Alert>{t('settings.trialExpired')}</Alert>
          </div>
        )}
      </section>

      <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
          {t('settings.appearance')}
        </h2>
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1">
          {THEMES.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={theme === value}
              onClick={() => setTheme(value)}
              className={
                'h-10 rounded-lg text-sm font-semibold transition-colors ' +
                (theme === value
                  ? 'bg-white text-gray-900 shadow-card'
                  : 'text-gray-600 hover:text-gray-900')
              }
            >
              {t(`settings.theme.${value}`)}
            </button>
          ))}
        </div>
      </section>

      <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
          {t('settings.notifications')}
        </h2>
        <p className="mb-3 text-sm text-gray-600">{t('settings.pushHint')}</p>
        {pushState && (
          <div className="mb-3">
            <Alert tone={pushState === 'granted' ? 'success' : 'error'}>
              {t(
                pushState === 'granted'
                  ? 'notifications.pushEnabled'
                  : pushState === 'denied'
                    ? 'notifications.pushDenied'
                    : 'notifications.pushUnsupported',
              )}
            </Alert>
          </div>
        )}
        <Button
          variant="secondary"
          onClick={() => {
            void enablePush().then(setPushState)
          }}
        >
          {t('notifications.enablePush')}
        </Button>
      </section>

      <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
          {t('settings.session')}
        </h2>
        <p className="mb-3 text-sm text-gray-600">{session?.user.email}</p>

        {session && session.memberships.length > 1 && (
          <div className="mb-3">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-widest text-gray-500">
              {t('settings.organization')}
            </p>
            <ul className="flex flex-col gap-1.5">
              {session.memberships.map((membership) => {
                const current = membership.organizationId === session.organizationId
                return (
                  <li key={membership.organizationId}>
                    <button
                      type="button"
                      disabled={current || switchOrganization.isPending}
                      aria-current={current ? 'true' : undefined}
                      onClick={() => switchOrganization.mutate(membership.organizationId)}
                      className={
                        'flex h-touch w-full items-center justify-between gap-3 rounded-xl border px-3.5 text-left text-sm transition-colors ' +
                        (current
                          ? 'border-brand-200 bg-brand-50 text-brand-900'
                          : 'border-gray-200 bg-white text-gray-800 hover:border-brand-300')
                      }
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">
                          {membership.organizationName}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {t(`settings.orgTypes.${membership.organizationType}`)}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs font-semibold text-brand-700">
                        {current ? t('settings.currentOrg') : t('settings.switchOrg')}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
            {switchOrganization.isError && (
              <div className="mt-2">
                <Alert>{translateError(switchOrganization.error)}</Alert>
              </div>
            )}
          </div>
        )}

        <Button variant="secondary" loading={logout.isPending} onClick={() => logout.mutate()}>
          {t('auth.logout')}
        </Button>
      </section>

      {isOwner && (
        <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
            {t('settings.yourData')}
          </h2>
          <p className="mb-3 text-sm text-gray-600">{t('settings.exportHint')}</p>
          <a
            href="/api/v1/account/export"
            className="btn inline-flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-5 text-base font-semibold text-gray-800 hover:bg-gray-50"
          >
            {t('settings.export')}
          </a>
        </section>
      )}

      {isOwner && (
        <section className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-red-800">
            {t('settings.dangerZone')}
          </h2>
          <p className="mb-3 text-sm text-red-900">{t('settings.deleteHint')}</p>

          {!deleting ? (
            <Button variant="secondary" onClick={() => setDeleting(true)}>
              {t('settings.delete')}
            </Button>
          ) : (
            <form
              className="flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault()
                deleteAccount.mutate()
              }}
            >
              {deleteAccount.isError && <Alert>{translateError(deleteAccount.error)}</Alert>}
              <Field
                label={t('settings.deleteConfirmLabel', { name: organizationName })}
                name="confirmation"
                autoFocus
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  loading={deleteAccount.isPending}
                  disabled={confirmation.trim() !== organizationName}
                >
                  {t('settings.deleteSubmit')}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setDeleting(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          )}
        </section>
      )}

      <p className="mt-4 text-center text-xs text-gray-400">
        <Link to="/terms" className="underline">
          {t('legal.terms')}
        </Link>
        {' · '}
        <Link to="/privacy" className="underline">
          {t('legal.privacy')}
        </Link>
      </p>
    </AppShell>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right font-medium text-gray-900">{value}</dd>
    </div>
  )
}
